import { useEffect, useState } from "react";

import { EditorState } from "prosemirror-state";
import { Node } from "prosemirror-model";
import {
  ProseMirror,
  ProseMirrorDoc,
  reactKeys,
} from "@handlewithcare/react-prosemirror";

import { LoadingSpinner } from "@newsela/angelou";

import { schema } from "@/ai/components/Editor/schemas";
import useLangstonContentGet from "@/content/useLangstonContentGet";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import { LangstonContentResponse } from "@/types/models/Content.interface";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery";
import { FLAVORS } from "@/components/ContentRow/ContentRow";
import wrapProsemirrorCardContainer from "@/bundles/utils/wrapProsemirrorCardContainer";
import HeaderActions from "../HeaderActions/HeaderActions";
import Viewmodel from "./components/Viewmodel/Viewmodel";
import { titleCss, prosemirrorContainerCss } from "./MainContent.styles";

type MainContentProps = {
  contentId: string | undefined;
};

export default function MainContent({ contentId }: MainContentProps) {
  const {
    data: content,
    error: contentRequestError,
    loading: contentLoading,
  }: UseRestQueryResponse<LangstonContentResponse> = useLangstonContentGet<LangstonContentResponse>(
    contentId as string,
  );
  const [currentDocument, setCurrentDocument] = useState<
    Record<string, any> | undefined
  >(undefined);
  const [currentEditorState, setCurrentEditorState] = useState<
    EditorState | undefined
  >(undefined);

  useEffect(() => {
    if (content && !contentLoading) {
      setCurrentDocument(content.page_viewmodel?.["documents"][0]);
    }
  }, [content, contentLoading]);

  useEffect(() => {
    if (currentDocument) {
      const currentDoc = currentDocument["body"];
      const docNode = Node.fromJSON(schema, currentDoc);
      const normalizedDoc = wrapProsemirrorCardContainer(docNode, schema);

      const editorState = EditorState.create({
        schema,
        doc: normalizedDoc,
        plugins: [reactKeys()],
      });
      setCurrentEditorState(editorState);
    }
  }, [currentDocument]);

  if (!currentEditorState || !currentDocument || contentLoading) {
    return <LoadingSpinner />;
  }

  if (contentRequestError) {
    return (
      <>
        <h1>Error when loading content.</h1>
      </>
    );
  }

  return (
    <BookmarkContextProvider>
      <h1 css={titleCss}>{currentDocument["title"]}</h1>
      <div css={prosemirrorContainerCss}>
        <HeaderActions content={content} />
        <ProseMirror
          editable={() => false}
          state={currentEditorState}
          nodeViews={{
            viewmodel: (props) => (
              // @ts-ignore
              <Viewmodel {...props} flavor={FLAVORS.BUNDLE_ROW} />
            ),
          }}
        >
          <ProseMirrorDoc />
        </ProseMirror>
      </div>
    </BookmarkContextProvider>
  );
}
